import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PlacesAutoComplete } from '@components/google-location/google-location.component';
import { TreatmentHistoryData } from '@models/medical-history.model';
import { AlertService } from '@services/alert.service';
import { DisplayService } from '@services/display.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { BehaviorSubject, take } from 'rxjs';

@Component({
  selector: 'gc-treatment-history',
  templateUrl: './treatment-history.component.html',
  styleUrls: ['./treatment-history.component.scss'],
  standalone: false,
})
export class TreatmentHistoryComponent implements OnInit, OnChanges {
  @Input() userId: number;
  @Input() treatmentHistory: TreatmentHistoryData[] = [];
  @Input() requestUuid: string | null = null;
  @Output() submittedStep = new EventEmitter<{ step: string; values: any }>();

  treatmentHistoryForm: FormGroup;
  historyListForm: FormGroup;
  panelOpenState = false;
  editIndex: number;
  isMobile$: BehaviorSubject<boolean>;
  placeAddress: string;
  constructor(
    private readonly fb: FormBuilder,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly alertService: AlertService,
    private readonly displayService: DisplayService,
  ) {}

  ngOnInit() {
    this.buildForms();
    this.isMobile$ = this.displayService.isMobile$;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['treatmentHistory'] && !changes['treatmentHistory'].firstChange) {
      this.buildForms();
    }
  }

  buildForms() {
    this.treatmentHistoryForm = this.initRow({
      begin_date: null,
      end_date: null,
      place: '',
      description: '',
      dr_note: '',
    });

    this.treatmentHistory = this.treatmentHistory ? this.treatmentHistory : [];

    this.historyListForm = this.fb.group({
      rowList: this.fb.array(this.treatmentHistory.map((treatment) => this.initRow(treatment))),
    });
  }

  checkDateValid(control: AbstractControl): boolean {
    const parentControl = control.parent.controls;
    const controlName = Object.keys(parentControl).find((name) => parentControl[name] === control);
    if (control.touched && control.value === '') {
      this.treatmentHistoryForm.get(controlName).setErrors({ invalid: true });
      return true;
    }
    return false;
  }

  initRow(treatment: TreatmentHistoryData): FormGroup {
    return this.fb.group({
      place: [treatment.place || ''],
      dr_note: [treatment.dr_note || ''],
      begin_date: [treatment.begin_date || '', Validators.required],
      end_date: [treatment.end_date || ''],
      description: [treatment.description || '', Validators.required],
    });
  }

  populateAddress(place: PlacesAutoComplete, index?: number) {
    if (index === undefined) {
      this.treatmentHistoryForm.get('place').patchValue(place.formattedAddress);
    } else {
      this.rowListControl.at(index).get('place').patchValue(place.formattedAddress);
    }
  }

  get rowListControl(): FormArray {
    return this.historyListForm.get('rowList') as FormArray;
  }

  addTreatment() {
    if (this.treatmentHistoryForm.valid) {
      this.rowListControl.push(this.initRow(this.treatmentHistoryForm.value));
      this.treatmentHistoryForm.reset();
    }
  }

  deleteTreatment(index: number) {
    this.rowListControl.removeAt(index);
  }

  saveToRequest() {
    this.medicalHistoryService
      .saveRequestMedicalHistory$(this.requestUuid, 'treatment_history', this.rowListControl.value)
      .pipe(take(1))
      .subscribe((request) => {
        if (request) {
          this.submittedStep.emit({
            step: 'treatmentHistory',
            values: this.rowListControl.value,
          });
          this.alertService.snackbar('treatment_history_saved');
        }
      });
  }

  saveToProfile() {
    this.medicalHistoryService
      .saveMedicalHistory$(this.userId, 'treatment_history', { user_id: this.userId, treatment_history: this.rowListControl.value })
      .subscribe(() => this.alertService.snackbar('treatment_history_saved'));
  }

  submit() {
    if (this.requestUuid) {
      this.saveToRequest();
    } else {
      this.saveToProfile();
    }
  }
}
